import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/free-solid-svg-icons';
import type { Post } from '../../types/post';
import { PostActions } from './PostActions';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { CommentList } from '../comment/CommentList';
import { CommentEditor } from '../comment/CommentEditor';
import { TagDisplay } from '../tag/TagDisplay';
import { PostLocationMap } from '../tag/PostLocationMap';
import { useAuth } from '../../auth/useAuth';
import { useComments } from '../../hooks/useComments';
import { PostModerationActions } from '../moderation/PostModerationActions';
import { FlagPostButton } from './FlagPostButton';
import { DeletePostButton } from './DeletePostButton';
import { ModerationHistory } from '../moderation/ModerationHistory';
import { useModerator } from '../../hooks/useModerator';
import { ShareButton } from '../share';
import { formatRelativeTime } from '../../utils/dateUtils';
import { getSightingType, getSightingTypeDisplay, getLocalizedPairingTypeLabel } from '../../types/post';
import type { Comment, CreateCommentRequest, UpdateCommentRequest } from '../../types/comment';
import { MessageCommentAuthorModal } from '../comment/MessageCommentAuthorModal';
import { UserAvatar } from '../common/UserAvatar';
import { ProfilePictureModal } from '../common/ProfilePictureModal';
import { useLanguage } from '../../i18n/useLanguage';
import { Linkify } from '../common/Linkify';
import { Button } from '../common/Button';
import { keycloakService } from '../../auth/keycloakService';

interface PostDetailProps {
  post: Post | null;
  loading: boolean;
  error: string | null;
  onDelete?: () => void;
  onFlagged?: () => void;
}

export const PostDetail: React.FC<PostDetailProps> = ({
  post,
  loading,
  error,
  onDelete,
  onFlagged,
}) => {
  const { t, language } = useLanguage();
  const sightingLanguage = language === 'de' ? 'de' : 'en';
  const { user, isAuthenticated } = useAuth();
  const isModerator = useModerator();
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedCommentForMessage, setSelectedCommentForMessage] = useState<Comment | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  const { comments, loading: commentsLoading, error: commentsError, fetchComments, createComment, updateComment, deleteComment } = useComments(post?.id);
  const [showDeletedComments, setShowDeletedComments] = useState(false);

  // Debug: Log moderator status (remove in production)
  React.useEffect(() => {
    if (isAuthenticated && post?.id) {
      console.log('[PostDetail Debug] User:', user);
      console.log('[PostDetail Debug] User roles:', user?.roles);
      console.log('[PostDetail Debug] Is moderator:', isModerator);
    }
  }, [isAuthenticated, user, isModerator, post?.id]);

  useEffect(() => {
    if (post?.id) {
      fetchComments(post.id, 0, 20, showDeletedComments && isModerator).catch((err) => {
        // Silently handle comment fetch errors - comments are optional
        console.warn('Failed to fetch comments:', err);
      });
    }
  }, [post?.id, fetchComments, showDeletedComments, isModerator]);

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!post) {
    return <div>{t('post.notFound')}</div>;
  }

  const isAuthor = isAuthenticated && user?.sub === post.authorId;

  const locationTag = post.tags?.find(
    (tag) => String(tag.tagType).toUpperCase() === 'LOCATION'
  );
  const locLat = locationTag?.locationLatitude;
  const locLon = locationTag?.locationLongitude;
  const showLocationMap =
    locLat !== undefined &&
    locLon !== undefined &&
    Number.isFinite(Number(locLat)) &&
    Number.isFinite(Number(locLon));

  const handleCommentSubmit = async (data: CreateCommentRequest | UpdateCommentRequest) => {
    if (!post.id) return;

    try {
      setIsSubmitting(true);
      if (editingComment) {
        await updateComment(editingComment.id, data);
        setEditingComment(null);
      } else {
        await createComment(post.id, data);
      }
      // Refresh comments
      await fetchComments(post.id);
    } catch (err) {
      console.error('Failed to save comment:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCommentEdit = (comment: Comment) => {
    setEditingComment(comment);
  };

  const handleCommentCancel = () => {
    setEditingComment(null);
  };

  const handleCommentDelete = async (commentId: string) => {
    if (!post.id) return;
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await deleteComment(commentId, post.id);
      await fetchComments(post.id);
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const handleMessageAuthor = (comment: Comment) => {
    setSelectedCommentForMessage(comment);
    setShowMessageModal(true);
  };

  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    setSelectedCommentForMessage(null);
  };

  return (
    <article className="post-detail">
      <div className="post-detail-header">
        {post.title && <h1 className="post-detail-title">{post.title}</h1>}
        <div className="post-detail-meta">
          <span className="post-detail-date">{formatRelativeTime(post.createdAt, language)}</span>
          {post.authorUsername && post.authorUsername.trim() && (
            <>
              <span className="post-detail-by">{t('post.by')}</span>
              <span className="post-detail-author">{post.authorUsername}</span>
              {post.authorId && (
                <UserAvatar 
                  userId={post.authorId} 
                  username={post.authorUsername} 
                  size={32}
                  onClick={() => setShowProfileModal(true)}
                />
              )}
            </>
          )}
        </div>
      </div>
      
      <div className="post-detail-content">
        <p><Linkify>{post.content}</Linkify></p>
      </div>

      {(() => {
        const type = getSightingType(post);
        const display = getSightingTypeDisplay(type, sightingLanguage);
        if (!display) return null;
        return (
          <div className="post-detail-sighting-type" data-sighting-type={type}>
            <strong>{t('post.sightingTypeLabel')}</strong> {display.emoji} {display.label}
          </div>
        );
      })()}

      {post.pairingType && (
        <div className="post-detail-pairing-type" data-pairing-type={post.pairingType}>
          <strong>{t('post.pairingTypeLabel')}</strong>{' '}
          {getLocalizedPairingTypeLabel(post.pairingType, sightingLanguage)}
        </div>
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="post-detail-tags">
          <TagDisplay tags={post.tags} />
        </div>
      )}

      {showLocationMap && (
        <section
          className="post-detail-location-map-section"
          aria-labelledby="post-detail-location-map-title"
        >
          <h3 id="post-detail-location-map-title" className="post-detail-location-map-title">
            {t('post.locationMapRegion')}
          </h3>
          <PostLocationMap
            key={`${post.id}-${locLat}-${locLon}`}
            latitude={Number(locLat)}
            longitude={Number(locLon)}
          />
        </section>
      )}

      <div className="post-card-actions">
        <div className="post-card-actions-left">
          <ShareButton postId={post.id} postTitle={post.title} variant="text" label={t('post.actionShare')} className="post-card-action-btn" />
          {isAuthenticated && !isAuthor && post.authorId && (
            <Link
              to={`/messages?userId=${encodeURIComponent(post.authorId)}&username=${encodeURIComponent(post.authorUsername || '')}`}
              className="post-card-action-btn"
            >
              <FontAwesomeIcon icon={faComments} className="post-card-action-icon" aria-hidden />
              <span>{t('post.actionContact')}</span>
            </Link>
          )}
        </div>
        <div className="post-card-actions-right">
          {isAuthor ? (
            <DeletePostButton postId={post.id} authorId={post.authorId} variant="text" label={t('post.actionDelete')} onDeleted={onDelete} />
          ) : (
            isAuthenticated && post.authorId && post.flagStatus !== 'PENDING' && (
              <FlagPostButton postId={post.id} authorId={post.authorId} variant="text" label={t('post.actionReport')} onFlagged={onFlagged} />
            )
          )}
        </div>
      </div>

      {(isAuthor || isModerator) && (
        <div className="post-detail-actions">
          {isAuthor && (
            <PostActions post={post} onDelete={onDelete} />
          )}
          {isModerator && (
            <PostModerationActions
              postId={post.id}
              isDeleted={post.deleted}
              onDeleted={onDelete}
              onRestored={onDelete}
            />
          )}
        </div>
      )}

      {isModerator && (
        <div className="post-detail-moderation-history">
          <ModerationHistory postId={post.id} />
        </div>
      )}

      <div className="post-detail-comments" id="comment-form">
          <div className="post-detail-comments-header">
            <h2>{t('post.commentsHeading')} ({comments.length})</h2>
            {isModerator && (
              <label className="show-deleted-toggle">
                <input
                  type="checkbox"
                  checked={showDeletedComments}
                  onChange={(e) => setShowDeletedComments(e.target.checked)}
                />
                <span>{t('post.showDeletedComments')}</span>
              </label>
            )}
          </div>
          
          {isAuthenticated ? (
            <div className="comment-section">
              <CommentEditor
                key={language}
                comment={editingComment || undefined}
                onSubmit={handleCommentSubmit}
                onCancel={editingComment ? handleCommentCancel : undefined}
                isLoading={isSubmitting}
              />
            </div>
          ) : (
            <div className="comment-login-cta">
              <p className="comment-login-cta__text">{t('post.commentsLoginInvite')}</p>
              <Button
                type="button"
                variant="primary"
                onClick={() =>
                  void keycloakService.login(`/posts/${post.id}#comment-form`)
                }
              >
                {t('common.login')}
              </Button>
            </div>
          )}

          <CommentList
            comments={comments}
            loading={commentsLoading}
            error={commentsError}
            isAuthenticated={isAuthenticated}
            onEdit={handleCommentEdit}
            onDelete={handleCommentDelete}
            onRefresh={() => {
              if (post.id) {
                fetchComments(post.id, 0, 20, showDeletedComments && isModerator).catch((err) => {
                  console.warn('Failed to refresh comments:', err);
                });
              }
            }}
            postAuthorId={post.authorId}
            onMessageAuthor={handleMessageAuthor}
          />
        </div>

      {selectedCommentForMessage && (
        <MessageCommentAuthorModal
          isOpen={showMessageModal}
          onClose={handleCloseMessageModal}
          comment={selectedCommentForMessage}
          postTitle={post.title}
        />
      )}
      
      {post.authorId && (
        <ProfilePictureModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          userId={post.authorId}
          username={post.authorUsername}
        />
      )}
    </article>
  );
};

